import { createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { getChangeDataGuid } from '../stowagePlanChangeUtils/stowageChangeDataTypeUtils';
import Decimal from 'decimal.js';
import { isNullOrUndefined } from 'src/utils/objectUtils';
import { getPortCallStatusDescription } from 'src/factory/Stowage/Rotation/RotationPortCallFactory';
import { getNestedValue, setNestedProperty } from 'src/utils/propertyUtils';
import { isManualCall } from '../stowageRotationUtils';
import { RotationElement } from './itemFindersUtils';
import { UUID } from 'crypto';

// Type Definitions
type Entity = Record<string, any>;
type PropertyDefinition = {
    key: string;
    description: string;
    nestedDescriptionKey?: string;
    nestedKey?: string;
    isGuid?: boolean;
    ignoreWhenManualEntry?: boolean;
    ignoreWhenManuallyEdited?: string[];
    setAttributesToNull?: string[];
    extraChecksIfNoChange?: PropertyDefinition[];
};
type IgnoredMergePropertyDefinition = {
    key: string;
    nestedKey?: string;
};

const ignoredMergePropertyDefinitions: Record<StowageChangeDataType, IgnoredMergePropertyDefinition[]> = {
    [StowageChangeDataType.StowageItem]: [],
    [StowageChangeDataType.BlockedTank]: [],
    [StowageChangeDataType.AdjustedCargoTemperature]: [],
    [StowageChangeDataType.Violation]: [],
    [StowageChangeDataType.RotationPortCall]: [],
    [StowageChangeDataType.RotationBerthCall]: [],
    [StowageChangeDataType.RotationCargoOperation]: [],
    [StowageChangeDataType.RotationCargo]: [{ key: 'productChanges', nestedKey: 'productChanges.maxAdjacentTemperature' }],
};

// Centralized property definitions
const propertyDefinitions: Record<StowageChangeDataType, PropertyDefinition[]> = {
    [StowageChangeDataType.StowageItem]: [
        { key: 'tankId', description: 'tank', nestedDescriptionKey: 'tank.tankName' },
        { key: 'cargoOperationGuid', description: 'cargo operation', nestedDescriptionKey: 'cargoOperation.cargoOperationGuid' },
        { key: 'weight', description: 'weight' },
        { key: 'isShipFigureAdjusted', description: 'ship figure adjusted' },
        { key: 'shipFigure', description: 'ship figure' },
    ],
    [StowageChangeDataType.BlockedTank]: [
        { key: 'tankId', description: 'tank' },
        { key: 'fromPortCallGuid', description: 'from port call' },
        { key: 'fromBerthCallGuid', description: 'from berth call' },
        { key: 'toPortCallGuid', description: 'to port call' },
        { key: 'toBerthCallGuid', description: 'to berth call' },
    ],
    [StowageChangeDataType.AdjustedCargoTemperature]: [
        { key: 'cargoGuid', description: 'cargo' },
        { key: 'tankId', description: 'tank' },
        { key: 'temperature', description: 'temperature' },
    ],
    [StowageChangeDataType.Violation]: [
        { key: 'stowageRuleId', description: 'stowage rule' },
        { key: 'type', description: 'type' },
        { key: 'status', description: 'status' },
        { key: 'statusChangedBy', description: 'changed by' },
        { key: 'description', description: 'description' },
        { key: 'portCallGuid', description: 'port call' },
        { key: 'berthCallGuid', description: 'berth call' },
        { key: 'cargoOperationGuid', description: 'cargo operation' },
        { key: 'cargoGuid', description: 'cargo' },
        { key: 'tankId', description: 'tank' },
        { key: 'adjacentTankId', description: 'adjacent tank' },
    ],
    [StowageChangeDataType.RotationPortCall]: [
        { key: 'portId', description: 'port', nestedDescriptionKey: 'portDisplayName' },
        { key: 'voyageNumber', description: 'voyage number' },
        { key: 'turnaroundVoyageNumber', description: 'turnaround voyage number' },
        { key: 'imosPortFunction', description: 'port function' },
        {
            key: 'portOrderNumber',
            description: 'Moved',
            setAttributesToNull: ['hasChangedSequence'],
            extraChecksIfNoChange: [{ key: 'portOrderNumber', nestedKey: 'hasChangedSequence.portOrderNumber', description: 'Moved' }],
        },
        { key: 'portCallStatus', description: 'status' },
        { key: 'voyageStatus', description: 'voyage status' },
    ],
    [StowageChangeDataType.RotationBerthCall]: [
        { key: 'berthId', description: 'berth', nestedDescriptionKey: 'berthName' },
        { key: 'portCallGuid', description: 'port call', nestedDescriptionKey: 'portCall.portDisplayName' },
        {
            key: 'berthSequenceNumber',
            description: 'Moved',
            setAttributesToNull: ['hasChangedSequence'],
            extraChecksIfNoChange: [{ key: 'originalSequenceNumber', nestedKey: 'hasChangedSequence.originalSequenceNumber', description: 'Moved' }],
        },
    ],
    [StowageChangeDataType.RotationCargoOperation]: [
        { key: 'operationType', description: 'operation type' },
        { key: 'demurrageRatePerDay', description: 'demurrage rate per day' },
        { key: 'pumpingRatePerHour', description: 'pumping rate per hour' },
        { key: 'cpDate', description: 'cp date' },
        { key: 'contractWeight', description: 'contract weight', ignoreWhenManualEntry: true },
        { key: 'nominatedWeight', description: 'nominated weight', ignoreWhenManualEntry: true },
        { key: 'billOfLadingWeight', description: 'B/L figure' },
        { key: 'cargoOperationSequenceNumber', description: 'Moved', ignoreWhenManualEntry: true, ignoreWhenManuallyEdited: ['hasChangedSequence'] },
        {
            key: 'berthCallGuid',
            description: 'berth call',
            nestedDescriptionKey: 'berthCall.berthName',
            ignoreWhenManualEntry: true,
            ignoreWhenManuallyEdited: ['hasChangedSequence'],
            isGuid: true,
        },
        {
            key: 'portCallGuid',
            description: 'port call',
            nestedDescriptionKey: 'berthCall.portCall.portDisplayName',
            ignoreWhenManualEntry: true,
            ignoreWhenManuallyEdited: ['hasChangedSequence'],
            isGuid: true,
        },
        { key: 'cargoGuid', nestedKey: 'cargo.cargoGuid', description: 'cargo', nestedDescriptionKey: 'cargo.product.tradeName', isGuid: true },
    ],
    [StowageChangeDataType.RotationCargo]: [
        { key: 'productId', description: 'trade name', nestedDescriptionKey: 'product.tradeName' },
        { key: 'chartererName', description: 'charterer' },
        { key: 'cargoNumber', description: 'cargo number' },
        { key: 'voyageNumber', description: 'voyage number' },
        { key: 'optionType', description: 'option type' },
        { key: 'optionPlusPercentage', description: 'option %' },
        { key: 'optionMinusPercentage', description: 'option %' },
        { key: 'cargoStatus', description: 'status' },
    ],
};

const getValueText = (value: any, entityType: StowageChangeDataType, key: string): string => {
    if (value === null || value === undefined) return '(no value)';
    if (entityType === StowageChangeDataType.RotationPortCall && key === 'portCallStatus') {
        return getPortCallStatusDescription(value);
    }
    return value;
};

const isManuallyEdited = (existingElement: any, propety: PropertyDefinition) => {
    const propertiesToCheck = propety.ignoreWhenManuallyEdited ?? [];
    return propertiesToCheck.some((property) => !isNullOrUndefined(existingElement[property]));
};

const compareEntities = <T extends Entity>(
    stowagePlanGuid: UUID,
    existingEntity: T,
    incomingEntity: T,
    entityType: StowageChangeDataType
): StowagePlanChange | null => {
    const properties = propertyDefinitions[entityType];
    const existingElement = existingEntity.rotationElement ?? existingEntity;
    const incomingElement = incomingEntity.rotationElement ?? incomingEntity;
    const changedProperties = properties
        .filter((property) => {
            if (
                (property.ignoreWhenManuallyEdited && isManuallyEdited(existingElement, property)) ||
                (property.ignoreWhenManualEntry && isManualCall(existingElement))
            )
                return false;
            if (existingElement[property.key] instanceof Decimal && incomingElement[property.key] instanceof Decimal) {
                return !existingElement[property.key].equals(incomingElement[property.key]);
            }
            if (property.isGuid) {
                return property.nestedKey
                    ? getNestedValue(existingElement, property.nestedKey) !== getNestedValue(incomingElement, property.nestedKey)
                    : existingElement[property.key] !== incomingElement[property.key];
            }
            const existingValue = property.nestedKey ? getNestedValue(existingElement, property.nestedKey) : existingElement[property.key];
            const incomingValue = property.nestedKey ? getNestedValue(incomingElement, property.nestedKey) : incomingElement[property.key];
            const hasChanged = (!isNullOrUndefined(existingValue) || !isNullOrUndefined(incomingValue)) && existingValue !== incomingValue;

            if (!hasChanged && property.extraChecksIfNoChange) {
                return property.extraChecksIfNoChange.some((extraCheck) => {
                    const existingExtraValue = extraCheck.nestedKey
                        ? getNestedValue(existingElement, extraCheck.nestedKey)
                        : existingElement[extraCheck.key];

                    if (isNullOrUndefined(existingExtraValue)) return false;
                    return (!isNullOrUndefined(existingExtraValue) || !isNullOrUndefined(incomingValue)) && existingExtraValue !== incomingValue;
                });
            }

            return hasChanged;
        })
        .reduce((acc, property) => {
            acc[property.key] = property.nestedKey ? getNestedValue(incomingElement, property.nestedKey) : incomingElement[property.key];
            if (!acc.extraData) acc.extraData = { ...(existingEntity.extraData ?? {}), ...(incomingEntity.extraData ?? {}) };
            if (!acc.extraData.descriptions) acc.extraData.descriptions = [];

            const _existingValue = property.nestedDescriptionKey
                ? getNestedValue(existingElement, property.nestedDescriptionKey)
                : existingElement[property.key];
            const _incomingValue = property.nestedDescriptionKey
                ? getNestedValue(incomingElement, property.nestedDescriptionKey)
                : incomingElement[property.key]; // TODO: NAMING-CONVENTION - Variable names start with underscore (_existingValue, _incomingValue) - underscore prefix is typically reserved for private/internal variables. Should use standard naming.
            const key = property.nestedKey ?? property.key;

            if (isManualCall(existingElement)) {
                acc.dataSource = 'IMOS';
            }
            if (property.setAttributesToNull) {
                property.setAttributesToNull.forEach((attribute) => {
                    if (acc[attribute] !== null) {
                        acc[attribute] = null;
                    }
                });
            }
            acc.extraData.descriptions.push(
                property.description !== 'Moved'
                    ? isNullOrUndefined(_existingValue)
                        ? `Added ${property.description} ${getValueText(_incomingValue, entityType, key)}`
                        : isNullOrUndefined(_incomingValue)
                            ? `Removed ${property.description} ${getValueText(_existingValue, entityType, key)}`
                            : `${property.description} changed from ${getValueText(_existingValue, entityType, key)} to ${getValueText(
                                _incomingValue,
                                entityType,
                                key
                            )}`
                    : property.description
            );
            return acc;
        }, {} as any);

    if (Object.keys(changedProperties).length > 0) {
        const guid = getChangeDataGuid(entityType, existingElement);
        changedProperties.extraData = {
            ...changedProperties.extraData,
            ...existingElement.extraData,
        };
        return createStowagePlanChange(stowagePlanGuid, guid, entityType, changedProperties, true);
    }

    return null;
};

export type CompareFunction = (stowagePlanGuid: UUID, existingEntity: Entity, incomingEntity: Entity) => StowagePlanChange | null;

const createCompareFunction = (entityType: StowageChangeDataType): CompareFunction => {
    return (stowagePlanGuid, existingEntity, incomingEntity) => {
        return compareEntities(stowagePlanGuid, existingEntity, incomingEntity, entityType);
    };
};

export const setIgnoredMergePropertiesInChangeData = (changeData: any, entityType: StowageChangeDataType, existingElement: RotationElement) => {
    const properties = ignoredMergePropertyDefinitions[entityType];
    properties.forEach((property) => {
        if (property.nestedKey) {
            setNestedProperty(changeData, property.nestedKey, getNestedValue(existingElement, property.nestedKey));
        } else {
            changeData[property.key] = existingElement[property.key];
        }
    });
};

export const comparePortCalls = createCompareFunction(StowageChangeDataType.RotationPortCall);
export const compareBerthCalls = createCompareFunction(StowageChangeDataType.RotationBerthCall);
export const compareCargoOperations = createCompareFunction(StowageChangeDataType.RotationCargoOperation);
export const compareCargoes = createCompareFunction(StowageChangeDataType.RotationCargo);
export const compareStowageItems = createCompareFunction(StowageChangeDataType.StowageItem);
export const compareBlockedTanks = createCompareFunction(StowageChangeDataType.BlockedTank);
export const compareAdjustedCargoTemperatures = createCompareFunction(StowageChangeDataType.AdjustedCargoTemperature);
export const compareViolations = createCompareFunction(StowageChangeDataType.Violation);
